import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';
import { useNavigate } from 'react-router-dom';
import Constructive1 from '../../assets/images/blog-images/Constructive1.png';
import Constructive2 from '../../assets/images/blog-images/Constructive2.png';
function Constructive() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container' >
            <div>
                <Breadcrumb route="/case-studies" pageTitle="Premier Financial Alliance" />
            </div>
            <section className='article-row-1' id='pfa-background'>
                <p>Procurement & Utility Solutions</p>
                <h2>Constructive Built Environment</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: Pennsylvania</span>
                    </div>
                </div>
            </section>


            <section className='article-intro-section'>
                <h1>How We Helped Constructive Built Environment Build the CART Platform</h1>
                <p>
                    Constructive Built Environment is a team of water industry professionals solving complex procurement challenges. They create value for utilities, vendors, and service providers by making procurement and deployment easier, more effective, and more transparent through solutions that drive optimization.
                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>Constructive Built Environment wanted to develop its CART (Centralized Access for Resources & Tracking) platform as quickly as possible and expedite the product launch, but they faced the resource constraints of being a startup.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We developed a custom procurement platform that facilitates high-value acquisitions for the utility sector. We didn't just deliver the platform. We actively shaped it, probing with thorough questions early on to unveil Constructive's hidden needs. Our proactiveness meant we could anticipate technical and UX bottlenecks, streamline our workflows, and even add features that ended up enhancing the usability and scalability as well as facilitating future integrations for CART. </p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>We kickstarted Constructive Built Environment's product by creating an MVP that users could actually enjoy using. This allowed Constructive to accelerate their go-to-market timeline, ensure smooth user engagement, and avoid additional overhead costs. We're supporting their long-term platform evolution and are in the process of developing the platform.
                    </p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>As a startup, Constructive Built Environment sought a tech partner who could deliver an MVP rapidly, ensure a frictionless UX/UI, and provide scalable solutions without hiring overhead. However, they faced significant hurdles:</h3>
                <ul>
                    <li>Finding economically viable expertise that could move fast. Hiring a full in-house team to build their user-centric, scalable platform for procurement would have been costly and inefficient, and managing multiple contractors would have threatened the process' speed—a critical element to securing market traction and early adopters.
                        Finding economically viable expertise that could move fast. Hiring a full in-house team to build their user-centric, scalable platform for procurement would have been costly and inefficient, and managing multiple contractors would have threatened the process' speed—a critical element to securing market traction and early adopters.</li>
                    <li>They needed an all-in-one partner, not fragmented support. Amalgam's umbrella model removed the need for coordination between multiple consultants, freeing up the Constructive team to focus on their growth.</li>
                </ul>
            </section>

            <section className='article-section-3'>
                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    We operated as an extension of the Constructive Built team, offering on-demand, full-scope development with the agility required for a startup.
                </p>
                <p>
                    We went beyond implementation from the get-go. We had strategic conversations with Constructive through which we clarified their vision, uncovered friction points, and proposed improvements that extended beyond the project's initial scope.
                </p>
                <p>
                    Unlike typical commodity marketplaces, CART enables direct engagement between utilities and vendors, streamlining the procurement process for premium goods and services.
                </p>
                <p>
                    After assessing the prototype and requirements, we selected a lightweight Bootstrap-based admin template as a foundation, to save time without sacrificing flexibility.
                </p>
                <p>
                    Then we custom-developed the UX/UI for a dozen pages covering:
                </p>
                <ul>
                    <li>Registration</li>
                    <li>Vendor listings</li>
                    <li>Service publishing and management</li>
                    <li>Lead contact creation and management</li>
                    <li>Project tracking</li>
                    <li>Invoicing and payments</li>
                </ul>
                <p>
                    Instead of a clunky page-by-page flow, we implemented a multi-step modal registration that keeps users on one screen, guiding them through the process smoothly and transparently with step indicators.
                </p>
                <p>
                    When the client ended up switching to Tailwind CSS and Gatsby for the live build, we ensured the transition was painless by adapting our work and aligning it closely to the new framework without compromising design integrity.
                </p>
                <p>
                    Our team ensured that the platform would deliver an intuitive and engaging experience to the users, all while avoiding any friction in procurement workflows.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>Conducted a DevOps assessment to define technical needs.</li>
                    <li>Asked clarifying questions to surface hidden UX issues and strengthen the product-market fit.</li>
                    <li>Designed the platform architecture for long-term scalability.</li>
                    <li>Created a clickable MVP with rapid iterations based on feedback.</li>
                    <li>Adjusted components and layouts based on real-time reviews from the client's team.</li>
                    <li>Connected screens to a simulated data environment to test and validate the full user journey before handoff.</li>
                </ul>

                <h3>The Results</h3>
                <ul>
                    <li>Constructive Built Environment received high-quality development and UX/UI design at a fraction of the cost of hiring full-time staff.</li>
                    <li>The CART platform delivers a frictionless procurement process, ensuring smooth vendor-utility transactions.</li>
                    <li>With our on-demand model, they could scale development efforts up or down based on evolving needs.</li>
                    <li>We accelerated Constructive Built Environment's go-to-market timeline. They were able to launch faster without the delays of internal hiring or multi-contractor management.</li>
                    <li>We're also implementing the platform and supporting Constructive with its post-MVP transitions, including framework and CMS changes, without slowing delivery or compromising quality.</li>
                </ul>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    Through flexible, scalable tech solutions, Amalgam enabled Constructive Built Environment to launch their MVP quickly, optimize procurement efficiency, and scale without unnecessary hiring costs.
                </p>

                <div className="screenshots-container">
                    <img
                        src={Constructive1}
                        alt="Constructive Dashboard"
                        className="screenshot screenshot-1"
                    />
                    <img
                        src={Constructive2}
                        alt="Constructive Projects View"
                        className="screenshot screenshot-2"
                    />
                </div>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>


            <section className='ourwork-row-14 blog-row-14'>

                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>

            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}

export default Constructive;